import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { pick, reject, complement, pipe } from 'ramda';

import { InstantCredit, mergeInstantCredit } from 'state/instantCredit';
import { CreditApplication } from 'state/creditApplication';
import { selectConsumerSearch } from 'state/consumerSearch';
import { selectTenant } from 'state/tenant';
import useAnalyticsVariables from 'hooks/useAnalyticVariables';
import useFmcHost from 'hooks/useFmcHost';
import { receiveAccountExists } from 'fmc-sdk';
import isPresent from 'utils/isPresent';

interface Options {
  /**
   * Whether this should send a AccountExists to the SDK. This may be set
   * to false when the caller is already sending the response to the SDK.
   */
  sendToSdk: boolean;
  /**
   * If not passed, this hook will use the consumerSearch state to determine if
   * consumerSearch validation has happened. If passed, this value will be used
   * instead.
   * This is useful for when the hook is called immediately after dispatching
   * mergeConsumerSearch (so the state has not been updated) or in hooks that
   * are considered validated but are not consumerSearch (e.g. dspRetrieval)
   */
  isValidated?: boolean;
}

/**
 * Perform common logic when receiving an existing account response.
 */
type ExistingAccountHandlerHook = () => (
  /**
   * The existing account response
   */
  response: Partial<InstantCredit>,
  options: Options,
  creditApplication?: Partial<CreditApplication>,
) => void;

export class ExistingAccountHandlerError extends Error {}

const useHandleExistingAccount: ExistingAccountHandlerHook = () => {
  const tenant = useSelector(selectTenant);
  const { result } = useSelector(selectConsumerSearch);
  const dispatch = useDispatch();
  const fmcDispatch = useFmcHost();

  const { setAnalyticsVariables } = useAnalyticsVariables();

  const handleExistingAccount = useCallback(
    (
      response: Partial<InstantCredit>,
      options: Options,
      creditApplication: Partial<CreditApplication> | undefined,
    ) => {
      if (!response.hasExistingAccount) {
        throw new ExistingAccountHandlerError(
          'handleExistingAccount on non-existing-account response',
        );
      }

      const { sendToSdk, isValidated } = options;

      setAnalyticsVariables({
        callId: response.callId,
        applicationId: response.applicationId,
        accountId: response.accountId,
      });

      if (sendToSdk) {
        const applicationData = pipe(
          pick(['mobilePhone', 'alternativePhone', 'emailAddress']),
          reject(complement(isPresent)),
        )(creditApplication || {});

        fmcDispatch(
          receiveAccountExists({
            message: 'An existing account exists',
            callId: response.callId || null,
            rewards: response.rewards,
            cardType: response.cardType,
            ...applicationData,
          }),
        );
      }

      const validated =
        isValidated !== undefined
          ? isValidated
          : result !== 'error' && result !== null;

      if (
        tenant.existingAccountBehavior === 'dsp' ||
        (tenant.existingAccountBehavior === 'validationRequired' && validated)
      ) {
        dispatch(mergeInstantCredit(response));
      }
    },
    [
      dispatch,
      fmcDispatch,
      result,
      setAnalyticsVariables,
      tenant.existingAccountBehavior,
    ],
  );

  return handleExistingAccount;
};

export default useHandleExistingAccount;
